import { useEffect, useMemo, useState } from "react";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { SelectInput } from "~/components/SelectInput";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    API_QUERY_BASE_URLS,
    ApiQuery,
    ApiQueryEndpoints,
    QueryResponse,
} from "~/modules/filters/types";
import {
    ChartTypes,
    TimeSeriesItem,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3";
import { LegendProps } from "~/modules/visualization/components/TimeSeriesChartV3/types";
import { LegendPosition, TimeseriesChartConfig } from "~/modules/charts/types";
import { buildTimeseriesQuery } from "../ChartForm/timeseries/timeseriesConfig";
import { TOKEN_TIMESERIES_INTERVAL_LABELS } from "./consts";
import { TOKEN_TIMESERIES_GROUP_BY_LABELS } from "~/modules/token_timeseries/consts";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";

// // // //

/**
 * Maps a legend position to the timeseries chart's legend alignment/layout.
 * The chart only supports top/bottom vertical alignment, so left/right legends
 * are anchored to the top.
 */
const LEGEND_LAYOUT: Record<
    LegendPosition,
    Pick<LegendProps, "align" | "layout" | "verticalAlign">
> = {
    top: { align: "center", layout: "horizontal", verticalAlign: "top" },
    bottom: { align: "center", layout: "horizontal", verticalAlign: "bottom" },
    left: { align: "left", layout: "vertical", verticalAlign: "top" },
    right: { align: "right", layout: "vertical", verticalAlign: "top" },
};

/**
 * TimeseriesChartRenderer
 * Renders a timeseries chart with optional group-by and interval controls.
 */
export function TimeseriesChartProvider(props: {
    title: string;
    description: string;
    config: TimeseriesChartConfig;
    height: number;
    children: (props: {
        title: string;
        description: string;
        isLoading: boolean;
        chartEl: React.ReactNode;
        loadingEl: React.ReactNode;
        customControls: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, description, config, height } = props;

    const groupByOptions =
        (config.groups_by_options as TokenTimeseriesGroupBy[]) ?? [];

    const intervalOptions =
        (config.intervals_options as AggregateQueryInterval[]) ?? [];

    const chartType = (config.chart_type as ChartTypes) ?? ChartTypes.area;

    const legendEnabled = (config.legend_enabled as boolean) ?? true;

    const legendPosition =
        (config.legend_position as LegendPosition) ?? "right";

    // Re-join the separately-stored measure_slug with the rest of the query.
    const query = useMemo(() => buildTimeseriesQuery(config), [config]);
    // Default to the viewer's first group-by option, falling back to the
    // groupBy configured on the query in the Data tab, then to protocol.
    const defaultGroupBy =
        config.default_group_by ??
        groupByOptions[0] ??
        (query.aggregate?.groupBy as TokenTimeseriesGroupBy | undefined) ??
        TokenTimeseriesGroupBy.assetClass;

    const [groupBy, setGroupBy] =
        useState<TokenTimeseriesGroupBy>(defaultGroupBy);

    useEffect(() => {
        setGroupBy(defaultGroupBy);
    }, [defaultGroupBy]);

    // Default to the viewer's first interval option, falling back to the
    // interval configured on the query in the Data tab, then to day.
    const defaultInterval =
        config.default_interval ??
        intervalOptions[0] ??
        query.aggregate?.interval ??
        AggregateQueryInterval.month;

    const [interval, setInterval] =
        useState<AggregateQueryInterval>(defaultInterval);

    useEffect(() => {
        setInterval(defaultInterval);
    }, [defaultInterval]);

    // Build the materialzed ApiQuery based on the query and the groupBy, interval, and groupLimit
    const materializedApiQuery: ApiQuery = {
        ...query,
        aggregate: {
            ...query.aggregate,
            groupBy: groupBy,
            interval: interval,
            groupLimit: 100, // NOTE - we may want to make this configurable
            aggregateFunction: AggregateFunctions.Sum,
        },
    };

    // Fetch Applied Query
    const queryReq = useApiQuery({
        endpoint: ApiQueryEndpoints.TokenTimeseriesV4,
        query: materializedApiQuery,
    });

    const loading = queryReq.isLoading || false;
    const data = queryReq.data as QueryResponse<TimeSeriesItem>;

    const showGroupByControl = groupByOptions.length >= 2;
    const showIntervalControl = intervalOptions.length >= 2;

    // Define custom controls if group-by or interval options are present
    const customControls = (
        <div className="w-full flex items-center gap-4 justify-end">
            {showGroupByControl && (
                <div className="flex items-center gap-3">
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Group By
                    </p>
                    <SelectInput
                        name="timeseries-chart--group-by-select"
                        value={groupBy}
                        size="sm"
                        disabled={queryReq.isLoading}
                        onChange={(updatedValue) => {
                            setGroupBy(updatedValue as TokenTimeseriesGroupBy);
                        }}
                        options={groupByOptions.map((option) => ({
                            value: option,
                            content: {
                                label: TOKEN_TIMESERIES_GROUP_BY_LABELS[option],
                            },
                        }))}
                    />
                </div>
            )}
            {showIntervalControl && (
                <div className="flex items-center gap-3">
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Interval
                    </p>
                    <SelectInput
                        name="timeseries-chart--interval-select"
                        value={interval}
                        size="sm"
                        disabled={queryReq.isLoading}
                        onChange={(updatedValue) => {
                            setInterval(updatedValue as AggregateQueryInterval);
                        }}
                        options={intervalOptions.map((option) => ({
                            value: option,
                            content: {
                                label: TOKEN_TIMESERIES_INTERVAL_LABELS[option],
                            },
                        }))}
                    />
                </div>
            )}

            <VisualizationApiToolsButton
                query={materializedApiQuery}
                apiDocsBaseUrl={
                    API_QUERY_BASE_URLS[ApiQueryEndpoints.TokenTimeseriesV4]
                }
            />

            <DataExportProvider>
                {({ isLoading, exportCsv }) => (
                    <DownloadButton
                        disabled={isLoading}
                        onClick={() => {
                            exportCsv({
                                exportTableName:
                                    ExportTableNames.token_timeseries_v4,
                                query: materializedApiQuery,
                            });
                        }}
                    />
                )}
            </DataExportProvider>
        </div>
    );

    const descriptionDisplay =
        groupByOptions.length === 1
            ? `Grouped by ${TOKEN_TIMESERIES_GROUP_BY_LABELS[groupBy]}`
            : description;

    const loadingEl = (
        <div
            className="flex items-center justify-center"
            style={{ height: `${height}px` }}
        >
            <LogoLoadingSpinner size={75} />
        </div>
    );

    const chartEl = (
        <TimeSeriesChart
            metrics={data?.results ?? []}
            metricsToTimeSeriesView={(metrics) =>
                metrics.map((item) => ({
                    item,
                    enabled: true,
                    chartType,
                }))
            }
            area={chartType === ChartTypes.area}
            showTotal={true}
            height={400}
            legend={{
                enabled: legendEnabled,
                ...LEGEND_LAYOUT[legendPosition],
            }}
        />
    );

    return (
        <>
            {props.children({
                title,
                description: descriptionDisplay,
                isLoading: loading,
                chartEl: chartEl,
                loadingEl: loadingEl,
                customControls: customControls,
            })}
        </>
    );
}
